'use client';

import { FilePenLine, Paperclip, Plus, SendHorizontal, Sparkles, X } from 'lucide-react';
import { documentLabels } from '../../document/document-types';
import { documentGuideFields } from '../../document/field-policy';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { installGenerateClickDiagnostics, logGenerateDebug } from '../generate-debug';

import type { DocumentType } from '../use-conversation';

export type DocumentInputGuide = Readonly<{
  fields: readonly string[];
}>;

export function getDocumentInputGuide(documentType: DocumentType): DocumentInputGuide {
  return { fields: documentGuideFields(documentType) };
}

export function buildDocumentInputTemplate(documentType: DocumentType): string {
  return `${documentGuideFields(documentType).map(field => `${field}：`).join('\n')}\n`;
}

export function appendDocumentInputTemplate(draft: string, documentType: DocumentType): string {
  const template = buildDocumentInputTemplate(documentType);
  return draft.trim() ? `${draft.trim()}\n\n${template}` : template;
}

export function shouldSubmitChatOnKey(
  event: Readonly<{ key: string; shiftKey: boolean; isComposing: boolean }>,
  chatAvailable: boolean,
): boolean {
  return chatAvailable && event.key === 'Enter' && !event.shiftKey && !event.isComposing;
}

export function detectDocumentTypeConflict(text: string, selected: DocumentType): DocumentType | null {
  const mentionsQuotation = /(?:\bquotation\b|\bquote\b|报价单|报价)/iu.test(text);
  const mentionsPi = /(?:\bpi\b|\bproforma\s+invoice\b|形式发票)/iu.test(text);
  const mentionsPo = /(?:\bpo\b|\bpurchase\s+order\b|采购订单|采购单)/iu.test(text);
  const mentionsSc = /(?:\bsc\s+(?:sales\s+)?(?:contract|confirmation)\b|\bsales\s+(?:contract|confirmation)\b|SC\s*销售(?:合同|确认书)|销售(?:合同|确认书))/iu.test(text);
  const mentionsSo = /\bSO\b/u.test(text) || /(?:\bshipping\s+order\b|订舱单|订舱|订舱确认单)/iu.test(text);
  const mentionsCi = /(?:\bCI\b|\bcommercial\s+invoice\b|商业发票)/iu.test(text);
  const mentionsPl = /(?:\bPL\b|\bpacking\s+list\b|装箱单)/iu.test(text);
  const mentionsCustoms = /(?:\bcustoms\s+invoice\b|海关发票)/iu.test(text);
  const mentionsCertificate = /(?:\bbeneficiary['’]?s?\s+certificate\b|受益人证明)/iu.test(text);
  const mentionsBl = /(?:\bB\/?L\b|海运提单|(?:生成|制作)\s*提单)/iu.test(text);
  const mentionsAwb = /(?:\bAWB\b|\bair\s+waybill\b|空运单)/iu.test(text);
  const mentionsManifest = /(?:\bmanifest\b|舱单)/iu.test(text);
  const mentionsAdvice = /(?:\bshipping\s+advice\b|装运通知)/iu.test(text);
  const mentionsSi = /(?:\bSI\b|\bshipping\s+instructions?\b|补料)/iu.test(text);
  const mentionsInsurance = /(?:\binsurance\s+policy\b|保险单|保险保单)/iu.test(text);
  const mentionsFumigation = /(?:\bfumigation\s+certificate\b|熏蒸证(?:书)?)/iu.test(text);
  const mentionsHealth = /(?:\bhealth\s+certificate\b|健康证(?:书)?|卫生证(?:书)?)/iu.test(text);
  const mentionsInspection = /(?:\binspection\s+certificate\b|检验证书|品质证书)/iu.test(text);
  const mentionsFormE = /(?:\bform\s*e(?=\b|东盟)|东盟(?:自贸区)?(?:优惠)?原产地证)/iu.test(text);
  const mentionsFormA = /(?:\b(?:gsp\s+)?form\s*a\b|普惠制(?:原产地)?证?)/iu.test(text);
  const mentionsCo = !mentionsFormA && !mentionsFormE && /(?:\bcertificate\s+of\s+origin\b|(?:CO\s*)?(?:一般)?原产地证)/iu.test(text);
  const mentioned = [mentionsFumigation, mentionsHealth, mentionsInspection, mentionsFormE, mentionsFormA, mentionsCo, mentionsInsurance, mentionsQuotation, mentionsPi, mentionsPo, mentionsSc, mentionsSo, mentionsCi, mentionsPl, mentionsCustoms, mentionsCertificate, mentionsBl, mentionsAwb, mentionsManifest, mentionsAdvice, mentionsSi].filter(Boolean).length;
  if (mentioned !== 1) return null;
  const indicated: DocumentType = mentionsFumigation ? 'FUMIGATION_CERTIFICATE' : mentionsHealth ? 'HEALTH_CERTIFICATE' : mentionsInspection ? 'INSPECTION_CERTIFICATE' : mentionsFormE ? 'ACFTA_FORM_E' : mentionsFormA ? 'GSP_FORM_A' : mentionsCo ? 'CERTIFICATE_OF_ORIGIN' : mentionsInsurance ? 'INSURANCE_POLICY' : mentionsSi ? 'SHIPPING_INSTRUCTION' : mentionsAdvice ? 'SHIPPING_ADVICE' : mentionsManifest ? 'MANIFEST' : mentionsAwb ? 'AIR_WAYBILL' : mentionsBl ? 'BILL_OF_LADING' : mentionsCertificate ? 'BENEFICIARY_CERTIFICATE' : mentionsCustoms ? 'CUSTOMS_INVOICE' : mentionsPl ? 'PACKING_LIST' : mentionsCi ? 'COMMERCIAL_INVOICE' : mentionsSo ? 'SHIPPING_ORDER' : mentionsSc ? 'SALES_CONTRACT' : mentionsPo ? 'PURCHASE_ORDER' : mentionsPi ? 'PROFORMA_INVOICE' : 'QUOTATION';
  return indicated === selected ? null : indicated;
}

export function HomeComposer({
  draft,
  documentType,
  pending,
  error,
  documentTypeLocked,
  aiStatus,
  aiReason,
  onDraftChange,
  onDocumentTypeChange,
  onStartNew,
  onSubmit,
  attachments = [],
  onAttachmentsChange = () => undefined,
  followup = false,
  complete = false,
  missingLabels = [],
  followupPanel,
}: Readonly<{
  draft: string;
  documentType: DocumentType;
  pending: boolean;
  error: string | null;
  documentTypeLocked: boolean;
  aiStatus: 'CHECKING' | 'AVAILABLE' | 'PAUSED' | 'UNCONFIGURED';
  aiReason: string | null;
  onDraftChange(value: string): void;
  onDocumentTypeChange(value: DocumentType): void;
  onStartNew(): void;
  onSubmit(): void;
  attachments?: readonly File[];
  onAttachmentsChange?(files: readonly File[]): void;
  followup?: boolean;
  complete?: boolean;
  missingLabels?: readonly string[];
  followupPanel?: React.ReactNode;
}>): React.ReactElement {
  const codePoints = Array.from(draft).length;
  const overLimit = codePoints > 6_000;
  const [targetConflict, setTargetConflict] = useState<DocumentType | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);
  const manualButtonRef = useRef<HTMLAnchorElement>(null);
  const chatAvailable = aiStatus === 'AVAILABLE';
  const statusLabel = aiStatus === 'AVAILABLE' ? 'AI 可用' : aiStatus === 'PAUSED' ? 'AI 暂停' : aiStatus === 'UNCONFIGURED' ? 'AI 未配置' : '检查中';
  const statusClass = aiStatus === 'AVAILABLE' ? 'status-success' : aiStatus === 'CHECKING' ? 'status-muted' : 'status-draft';
  const inputGuide = getDocumentInputGuide(documentType);
  const canSubmit = chatAvailable && !pending && Boolean(draft.trim() || attachments.length) && !overLimit;
  const serviceMessage = chatAvailable || aiStatus === 'CHECKING'
    ? null
    : `${aiReason ?? (aiStatus === 'PAUSED' ? 'AI 服务当前暂停。' : 'AI Provider 尚未配置。')}可继续使用手工填写。`;
  useEffect(() => {
    if (!generateButtonRef.current) return;
    return installGenerateClickDiagnostics(generateButtonRef.current, manualButtonRef.current);
  }, []);
  useEffect(() => {
    logGenerateDebug('composer.state', { canSubmit, aiStatus, pending, codePoints, attachmentCount: attachments.length, overLimit, documentType });
  }, [canSubmit, aiStatus, pending, codePoints, attachments.length, overLimit, documentType]);
  const requestSubmit = (source: 'button' | 'keyboard'): void => {
    logGenerateDebug('requestSubmit.enter', { source, canSubmit, aiStatus, pending, codePoints, attachmentCount: attachments.length, overLimit, documentType });
    if (!canSubmit) {
      logGenerateDebug('requestSubmit.blocked', { aiUnavailable: !chatAvailable, pending, empty: !draft.trim() && attachments.length === 0, overLimit });
      return;
    }
    const conflict = detectDocumentTypeConflict(draft, documentType);
    if (conflict) {
      logGenerateDebug('requestSubmit.conflict', { selected: documentType, detected: conflict });
      setTargetConflict(conflict);
      return;
    }
    setTargetConflict(null);
    logGenerateDebug('requestSubmit.forward', { source });
    onSubmit();
  };
  return (
    <section className="compose-band chat-composer" aria-labelledby="compose-title">
      <div className="compose-header">
        <div>
          <h2 id="compose-title">Chat 制单</h2>
        </div>
        <span className={`status-badge ${statusClass}`}><Sparkles size={14} />{statusLabel}</span>
      </div>
      <div className="chat-input-guide" id="chat-input-guide" role="status" aria-live="polite">
        <div className="chat-input-guide-copy">
          <strong>{documentLabels[documentType]}：建议提供</strong>
          <span>{inputGuide.fields.join('、')}</span>
        </div>
        <button className="secondary-button chat-template-button" type="button" disabled={pending} onClick={() => {
          onDraftChange(appendDocumentInputTemplate(draft, documentType));
        }}>插入输入模板</button>
      </div>
      {followupPanel}
      <textarea
        id="trade-chat"
        rows={6}
        value={draft}
        aria-label="交易信息"
        onChange={(event) => { setTargetConflict(null); onDraftChange(event.target.value); }}
        onKeyDown={(event) => {
          if (shouldSubmitChatOnKey(event.nativeEvent, chatAvailable)) {
            event.preventDefault();
            requestSubmit('keyboard');
          }
        }}
        placeholder={followup ? missingLabels.length ? `请补充${missingLabels.slice(0, 3).join('、')}。之前的信息会保留。` : '继续补充或修改信息，例如“第 1 项数量改为 800”。之前的信息会保留。' : undefined}
        aria-describedby={`chat-input-guide chat-input-help chat-input-count${error ? ' chat-input-error' : ''}`}
        aria-invalid={overLimit || Boolean(error)}
        disabled={pending}
      />
      <div className="compose-help-row">
        <span id="chat-input-help">Enter 发送 · Shift+Enter 换行 · 支持中文和 Emoji</span>
        <span id="chat-input-count" className={overLimit ? 'text-danger' : ''}>字数 {codePoints.toLocaleString('zh-CN')} / 6,000</span>
      </div>
      <div className="chat-attachment-row">
        {!complete ? <><input ref={fileInputRef} className="visually-hidden" type="file" multiple accept=".docx,.xlsx,.xls,.pdf,.jpg,.jpeg,.png" onChange={(event) => { const selected = Array.from(event.target.files ?? []); const next = [...attachments, ...selected]; const totalBytes = next.reduce((sum, file) => sum + file.size, 0); if (next.length > 5) setAttachmentError('一次最多上传 5 个附件。'); else if (totalBytes > 20 * 1024 * 1024) setAttachmentError('附件总大小不能超过 20 MB。'); else { setAttachmentError(null); onAttachmentsChange(next); } event.currentTarget.value = ''; }} />
          <button className="secondary-button" type="button" disabled={pending} onClick={() => fileInputRef.current?.click()}><Paperclip size={16} />上传附件</button>
          <span className="helper-text">支持 DOCX、Excel、PDF、JPG、PNG，最多 5 个 / 总计 20 MB</span></> : null}
        <div className="chat-attachment-actions">
          <Link ref={manualButtonRef} className="secondary-button" href={`/trades/new?type=${documentType}`}><FilePenLine size={17} />手工填写</Link>
          {documentTypeLocked ? <button className="secondary-button" type="button" disabled={pending} onClick={onStartNew}><Plus size={17} />开始新交易</button> : null}
          <button ref={generateButtonRef} className="primary-button" type="button" onClick={(event) => { logGenerateDebug('button.click', { isTrusted: event.isTrusted, defaultPrevented: event.defaultPrevented, x: event.clientX, y: event.clientY }); requestSubmit('button'); }} disabled={!canSubmit}>
            <SendHorizontal size={18} /><span className="primary-button-label">{pending ? '处理中…' : followup ? '提交补充' : '生成'}</span>
          </button>
        </div>
      </div>
      {!complete && attachments.length ? <div className="chat-attachments" aria-label="待发送附件">{attachments.map((file, index) => <span className="chat-attachment-chip" key={`${file.name}-${file.lastModified}-${index}`}><span>{file.name}（{formatFileSize(file.size)}）</span><button type="button" aria-label={`移除 ${file.name}`} onClick={() => { setAttachmentError(null); onAttachmentsChange(attachments.filter((_, itemIndex) => index !== itemIndex)); }}><X size={14} /></button></span>)}</div> : null}
      {!complete && attachmentError ? <p className="form-error" role="alert">{attachmentError}</p> : null}
      {targetConflict ? <div className="target-confirmation" role="alertdialog" aria-labelledby="target-confirmation-title">
        <div><strong id="target-confirmation-title">确认生成目标</strong><span>文字中明确提到 {targetLabel(targetConflict)}，当前选择是 {targetLabel(documentType)}。</span></div>
        <div className="target-confirmation-actions">
          <button className="secondary-button" type="button" onClick={() => { setTargetConflict(null); onDocumentTypeChange(targetConflict); }}>改为 {targetLabel(targetConflict)}</button>
          <button className="primary-button" type="button" disabled={!canSubmit} onClick={() => { if (!canSubmit) return; setTargetConflict(null); onSubmit(); }}>仍按 {targetLabel(documentType)} 生成</button>
        </div>
      </div> : null}
      {serviceMessage ? <p className="compose-service-note" role="status">{serviceMessage}</p> : null}
      {error ? <p id="chat-input-error" className="form-error" role="alert">{error}</p> : null}
    </section>
  );
}

function formatFileSize(size: number): string { return size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`; }

function targetLabel(documentType: DocumentType): string {
  return documentLabels[documentType];
}
